import { createSelector } from "reselect"
import { get, chain, map, join, first, capitalize, noop, toLower, includes } from "lodash"
import { getAssetClassFromMic } from "helpers/securities"
import { getExcelValue } from "helpers/csv"
import { getFactorsCategories, getSelectedCategory } from "./categories"
import { getAllColumns } from "./columns"
import { getDecompositionType, getRebalanceDate } from "."
import { getIsIntraday, getIntradayAsOf } from "./intraday"
import { getIsActive, getPortfolio } from "modules/portfolio-root/selectors/portfolios"
import { getSelectedContentSet } from "./exposure"
import { getSortedContributorsData, getTradedPositions } from "./data"
import { getRawComposition } from "./positions"
import { getTradeType } from "helpers/simulator"

const TRADE_TYPES_ORDER = {
  buy: 1,
  sell: 2,
  short: 3,
  cover: 4,
}

const TRADE_FILE_HEADERS = [
  "Trade Type",
  "Identifier",
  "MIC",
  "Bloomberg Ticker",
  "Country",
  "Asset Class",
  "Currency",
  "Industry",
  "Original Position Size",
  "Rebalanced Position Size",
  "Position $ Change",
  "Original Equity %",
  "Rebalanced Equity %",
  "Equity % Change",
]

const DATA_DOWNLOAD_DESCRIPTORS = [
  { label: "Identifier", valueGetter: contributor => get(contributor, "ticker") || get(contributor, "id") },
  { label: "Mic", valueGetter: getContributorMic },
  { label: "Bloomberg Ticker", valueGetter: contributor => get(contributor, "bloombergTicker") },
  { label: "Country", valueGetter: contributor => get(contributor, "country") },
  { label: "Currency", valueGetter: contributor => get(contributor, "currency") },
  { label: "Industry", valueGetter: contributor => get(contributor, "classification.name") },
]

function filenameTypeFragment(decompositionType, selectedContentSet) {
  if (toLower(decompositionType) === "marginal") {
    // Would give Marginal Marginal Contribution in the name otherwise...
    return ""
  }
  if (selectedContentSet) {
    return selectedContentSet.name
  }
  return capitalize(decompositionType)
}

function filenameCategoryFragment(decompositionType, selectedCategory, factorCategories) {
  if (toLower(decompositionType) === "marginal") {
    return "Marginal Contribution"
  }

  let fragment

  if (selectedCategory) {
    fragment = `${capitalize(selectedCategory.label)}`
  } else if (decompositionType !== "risk") {
    fragment = get(first(factorCategories), "label")
  }

  return chain([fragment, "Factors"])
    .compact()
    .join(" ")
    .value()
}

function filenameDateFragment(rebalanceDate, isIntraday, intradayAsOf) {
  if (isIntraday) {
    return `${rebalanceDate} as of ${intradayAsOf}`
  }
  return rebalanceDate
}

function filenameActiveFragment(isActive) {
  return isActive ? "Active" : "Absolute"
}

function getContributorMic(contributor) {
  const mic = get(contributor, "mic")
  const type = getAssetClassFromMic(mic)
  if (type !== "equity") {
    return ""
  }
  return mic
}

function formatExcelColumn(column, contributor) {
  const { getter, outputFormat, path, id } = column
  let value = (getter ?? noop)(contributor, path, id)
  // positionPercentEquity and activePercentEquity are the only percentages on base 100 instead of 1
  if (value && includes(["positionPercentEquity", "activePercentEquity"], id)) {
    value /= 100
  }
  return getExcelValue(value, { percent: outputFormat === "percent" })
}

const getDownloadDataFileName = createSelector(
  [getPortfolio, getDecompositionType, getSelectedCategory, getFactorsCategories, getRebalanceDate, getIsIntraday, getIntradayAsOf, getSelectedContentSet, getIsActive],
  (portfolio, decompositionType, selectedCategory, factorCategories, rebalanceDate, isIntraday, intradayAsOf, selectedContentSet, isActive) => join([
    get(portfolio, "name"),
    `${filenameTypeFragment(decompositionType, selectedContentSet)} ${filenameCategoryFragment(decompositionType, selectedCategory, factorCategories)}`,
    filenameActiveFragment(isActive),
    filenameDateFragment(rebalanceDate, isIntraday, intradayAsOf),
  ], " - "),
)

export const getDownloadData = createSelector(
  [getSortedContributorsData, getAllColumns, getDownloadDataFileName],
  (sortedData, columns, fileName) => {
    const headers = [...map(DATA_DOWNLOAD_DESCRIPTORS, "label"), ...map(columns, "label")]
    const data = chain(sortedData)
      .map()
      .flatten()
      .map(contributor => [
        ...map(DATA_DOWNLOAD_DESCRIPTORS, ({ valueGetter }) => valueGetter(contributor)),
        ...map(columns, (column) => formatExcelColumn(column, contributor))])
      .value()

    return {
      fileName,
      type: "Simulator Data Download",
      data: [
        headers,
        ...data,
      ] }
  },
)

export const getTradeDownload = createSelector(
  [getTradedPositions, getRawComposition],
  (trades, { referenceEquity, gmv }) => {
    const workingEquity = referenceEquity || gmv
    return [
      TRADE_FILE_HEADERS,
      ...chain(trades)
        .map(({ ticker, mic, securityPercentEquity, tradePercentDelta, bloombergTicker, country, assetClass, currency, classification }) => ([
          getTradeType({ securityPercentEquity, tradePercentDelta }),
          ticker,
          mic,
          bloombergTicker,
          country,
          assetClass,
          currency,
          classification,
          getExcelValue(securityPercentEquity * workingEquity),
          getExcelValue((securityPercentEquity + tradePercentDelta) * workingEquity),
          getExcelValue((tradePercentDelta * workingEquity)),
          getExcelValue(securityPercentEquity, { percent: true }),
          getExcelValue(securityPercentEquity + tradePercentDelta, { percent: true }),
          getExcelValue(tradePercentDelta, { percent: true }),
        ]))
        .sortBy([({ type }) => TRADE_TYPES_ORDER[type], "ticker"])
        .value(),
    ]
  },
)
